import * as React from 'react';

import { cn } from '../../lib/cn';
import { ActivityIndicator, type ActivityIndicatorProps } from './activity-indicator';

/**
 * ActivityTrace — lays out a sequence of activity lines + answer paragraphs
 * INSIDE a single assistant bubble (deep_agent / sub-agent turns, where text
 * and activity interleave: "먼저 찾아보겠습니다." → ⟳ tool → "정리하면…" → answer).
 *
 * Grouping rules (the spec):
 * - Consecutive activity/`role:"process"` lines are bound by a muted left
 *   timeline rail (`--color-status-rail`) — the "과정(process)" zone.
 * - `role:"answer"` text breaks out of the rail as full-width normal text —
 *   the process↔answer visual hierarchy.
 * - A nested sub-agent = an activity item carrying `trace` children; it renders
 *   its own indented sub-rail (recursive), capped at 3 visible levels.
 *
 * Token-only; flows with roundness/density knobs and preset × light/dark.
 */
export type ActivityTraceItem =
  | { text?: string; node?: React.ReactNode; role?: 'answer' | 'process'; activity?: undefined }
  | { activity: ActivityIndicatorProps; trace?: ActivityTraceItem[] };

export interface ActivityTraceProps {
  /** Ordered mix of answer paragraphs, process lines, and activity lines. */
  items: ActivityTraceItem[];
  /** Nesting depth (managed internally for sub-agents; leave unset at the top). */
  depth?: number;
  className?: string;
}

const MAX_DEPTH = 2; // 0-indexed → 3 visible levels

const RAIL =
  'flex flex-col gap-[var(--status-line-gap)] ml-[var(--status-rail-indent)] ' +
  'pl-[var(--status-rail-gap)] border-l-[1.5px] border-[var(--color-status-rail)]';

function isActivity(
  item: ActivityTraceItem,
): item is { activity: ActivityIndicatorProps; trace?: ActivityTraceItem[] } {
  return (item as { activity?: unknown }).activity != null;
}

export function ActivityTrace({ items, depth = 0, className }: ActivityTraceProps) {
  const blocks: React.ReactNode[] = [];
  let buffer: React.ReactNode[] = [];
  let groupKey = 0;

  const flush = () => {
    if (!buffer.length) return;
    const b = buffer;
    blocks.push(
      <div key={`g${groupKey++}`} className={RAIL}>
        {b}
      </div>,
    );
    buffer = [];
  };

  items.forEach((item, i) => {
    if (!isActivity(item)) {
      // Answer text/node → break the rail, render full-width.
      if (item.role == null || item.role === 'answer') {
        flush();
        blocks.push(
          item.node != null ? (
            <div key={`a${i}`} className="min-w-0 text-bubble-bot-fg">
              {item.node}
            </div>
          ) : (
            <p
              key={`a${i}`}
              className="m-0 text-[length:var(--text-base)] leading-[var(--leading-normal)] text-bubble-bot-fg"
            >
              {item.text}
            </p>
          ),
        );
        return;
      }
      // Process text → muted line inside the rail.
      buffer.push(
        <span
          key={`p${i}`}
          className="text-[length:var(--status-font)] leading-[var(--leading-snug)] text-[var(--color-status-fg)]"
        >
          {item.node ?? item.text}
        </span>,
      );
      return;
    }

    // Activity line (+ optional nested sub-agent trace).
    const glyph =
      item.activity.glyph ?? (item.activity.status === 'done' ? 'icon' : 'spinner');
    buffer.push(<ActivityIndicator key={`i${i}`} variant="bare" glyph={glyph} {...item.activity} />);

    if (item.trace && item.trace.length) {
      if (depth < MAX_DEPTH) {
        buffer.push(<ActivityTrace key={`t${i}`} items={item.trace} depth={depth + 1} />);
      } else {
        // depth cap reached — flatten remaining activity lines without a new rail
        item.trace.forEach((sub, j) => {
          if (isActivity(sub)) {
            buffer.push(
              <ActivityIndicator
                key={`i${i}-${j}`}
                variant="bare"
                glyph={sub.activity.status === 'done' ? 'icon' : 'spinner'}
                {...sub.activity}
              />,
            );
          }
        });
      }
    }
  });
  flush();

  return (
    <div className={cn('flex min-w-0 flex-col gap-[var(--status-block-gap)]', className)}>
      {blocks}
    </div>
  );
}
